import { useEffect, useState } from "react";
import { heicTo } from "heic-to";

import { type ComparePair } from "@fitness-app/client-shared/features/measurements/compare-photo";

import { isActualHeicBlob, isHeicUrl } from "~/hooks/useHeicImage";
import { type PhotoCompareImages } from "./drawPhotoCompare";

const cache = new Map<string, HTMLImageElement>();

const decode = (src: string, crossOrigin: boolean): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    if (crossOrigin) {
      // Signed storage URLs send CORS headers; without them a drawn photo would taint the canvas.
      image.crossOrigin = "anonymous";
    }
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Cannot load ${src}`));
    image.src = src;
  });

/**
 * Chrome and Firefox cannot decode HEIC, so such a photo is fetched and converted to JPEG first
 * (the same path as `useHeicImage`). Files already converted server-side keep their bytes.
 */
const decodeHeic = async (url: string): Promise<HTMLImageElement> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status}`);
  }
  const blob = await response.blob();
  const drawable = (await isActualHeicBlob(blob)) ? await heicTo({ blob, type: "image/jpeg", quality: 0.85 }) : blob;
  const objectUrl = URL.createObjectURL(drawable);
  try {
    return await decode(objectUrl, false);
  } finally {
    // The decoded element stays drawable after the URL is revoked.
    URL.revokeObjectURL(objectUrl);
  }
};

const loadImage = async (url: string): Promise<HTMLImageElement> => {
  const cached = cache.get(url);
  if (cached) {
    return cached;
  }
  const image = isHeicUrl(url) ? await decodeHeic(url) : await decode(url, true);
  cache.set(url, image);
  return image;
};

/** Resolves the pair's photos into drawable images; a slot stays null until its photo is in. */
export const useCompareImages = (
  pair: ComparePair,
): { images: PhotoCompareImages; loading: boolean; failed: boolean; retry: () => void } => {
  const [images, setImages] = useState<PhotoCompareImages>({ before: null, after: null });
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  // Bumped by `retry` so a transient failure can be re-fetched without closing the modal;
  // failures are never cached.
  const [attempt, setAttempt] = useState(0);
  const beforeUrl = pair.before?.url ?? null;
  const afterUrl = pair.after?.url ?? null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    const load = (url: string | null) => (url ? loadImage(url).catch(() => null) : Promise.resolve(null));
    void Promise.all([load(beforeUrl), load(afterUrl)]).then(([before, after]) => {
      if (!cancelled) {
        setImages({ before, after });
        setFailed((beforeUrl !== null && before === null) || (afterUrl !== null && after === null));
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [beforeUrl, afterUrl, attempt]);

  return { images, loading, failed, retry: () => setAttempt((current) => current + 1) };
};
